import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { CalendarDays, Clock, MapPin, Tag } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import axios from "axios";
import { format, parseISO, isSameDay } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryColors = {
  service: "bg-red-50 text-red-700 border-red-200",
  youth: "bg-blue-50 text-blue-700 border-blue-200",
  outreach: "bg-green-50 text-green-700 border-green-200",
  prayer: "bg-amber-50 text-amber-700 border-amber-200",
  special: "bg-purple-50 text-purple-700 border-purple-200",
};

export const EventsSection = () => {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/events`);
        setEvents(res.data);
      } catch (err) {
        console.error("Failed to fetch events:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const eventDates = events.map((e) => {
    try { return parseISO(e.date); } catch { return null; }
  }).filter(Boolean);

  const filteredEvents = selectedDate
    ? events.filter((e) => {
        try { return isSameDay(parseISO(e.date), selectedDate); } catch { return false; }
      })
    : events;

  const displayEvents = filteredEvents.length > 0 ? filteredEvents : events.slice(0, 5);

  return (
    <section id="events" data-testid="events-section" className="py-20 md:py-32 bg-gradient-to-br from-amber-50/50 via-white to-red-50/30 relative overflow-hidden">
      <div className="bubble-decoration bg-amber-400 w-64 h-64 -top-32 right-10 animate-pulse-soft" />
      <div className="bubble-decoration bg-red-300 w-40 h-40 bottom-10 -left-20 animate-pulse-soft" style={{ animationDelay: "1s" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="font-accent text-2xl text-amber-600">Don't Miss Out</span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-stone-900 mt-2">
            Upcoming <span className="text-red-600">Events</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-[340px_1fr] gap-10 items-start">
          {/* Calendar */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-stone-100"
            data-testid="events-calendar"
          >
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              modifiers={{ hasEvent: eventDates }}
              modifiersStyles={{
                hasEvent: {
                  backgroundColor: "#FEE2E2",
                  color: "#DC2626",
                  fontWeight: 700,
                  borderRadius: "9999px",
                },
              }}
              className="rounded-2xl"
            />
            <p className="text-xs text-stone-400 mt-4 text-center">
              Red highlighted dates have events
            </p>
          </motion.div>

          {/* Events list */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-4"
          >
            {filteredEvents.length > 0 && (
              <p className="text-sm text-stone-500 font-medium mb-2">
                Events on {format(selectedDate, "MMMM d, yyyy")}
              </p>
            )}
            {filteredEvents.length === 0 && (
              <p className="text-sm text-stone-400 mb-4">
                No events on selected date. Showing upcoming events:
              </p>
            )}

            {loading ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className="rounded-2xl bg-white h-28 animate-pulse border border-stone-100" />
              ))
            ) : displayEvents.length === 0 ? (
              <div className="text-center py-12 text-stone-400">
                <CalendarDays size={40} className="mx-auto mb-3 opacity-50" />
                <p>No upcoming events yet</p>
              </div>
            ) : (
              displayEvents.map((event, i) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.3 + i * 0.08 }}
                  className="bubbly-card bg-white p-6 rounded-2xl border border-stone-100 shadow-[0_4px_20px_rgb(0,0,0,0.04)] flex flex-col sm:flex-row gap-4"
                  data-testid={`event-card-${event.id}`}
                >
                  {/* Date badge */}
                  <div className="shrink-0 w-16 h-16 rounded-2xl bg-red-50 flex flex-col items-center justify-center">
                    <span className="text-xs font-semibold text-red-600 uppercase">
                      {(() => { try { return format(parseISO(event.date), "MMM"); } catch { return ""; } })()}
                    </span>
                    <span className="text-xl font-bold text-red-700">
                      {(() => { try { return format(parseISO(event.date), "d"); } catch { return ""; } })()}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-bold text-stone-900">{event.title}</h3>
                      <span className={`shrink-0 text-xs font-semibold px-3 py-1 rounded-full border ${categoryColors[event.category] || categoryColors.service}`}>
                        {event.category}
                      </span>
                    </div>
                    {event.description && (
                      <p className="text-sm text-stone-500 mt-1 line-clamp-1">{event.description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 mt-3 text-xs text-stone-400">
                      <span className="flex items-center gap-1"><Clock size={12} /> {event.time}</span>
                      <span className="flex items-center gap-1"><MapPin size={12} /> {event.location}</span>
                      {event.is_recurring && <span className="flex items-center gap-1"><Tag size={12} /> Recurring</span>}
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
